import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Users, RefreshCw, Loader2, ShieldAlert, Eye, Archive,
  Inbox, X, ShieldCheck, UserPlus, FileSpreadsheet, Factory, Download,
  TrendingUp, CalendarClock, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/adminApi";
import { LeadsManager, LEAD_STATUS, INDUSTRIA_LABELS } from "@/components/admin/LeadsManager";
import { AffiliatesManager } from "@/components/admin/AffiliatesManager";
import { ActivePoliciesManager } from "@/components/admin/ActivePoliciesManager";
import { NewLeadDialog } from "@/components/admin/NewLeadDialog";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso || "-"; }
};

const leadSig = (arr) =>
  (arr || [])
    .map((d) => `${d.id}:${d.status}:${d.status_changed_at || ""}:${d.archived ? 1 : 0}:${d.aseguradora || ""}:${d.poliza_vence || ""}:${(d.notes || []).length}:${d.commission_amount ?? ""}:${d.commission_paid ? 1 : 0}:${d.cotizacion ? 1 : 0}:${d.current_policy ? 1 : 0}:${(d.tipo_industria || []).join(",")}:${d.nombre || ""}:${d.rut || ""}:${d.email || ""}:${d.celular || ""}`)
    .join("|");
const claimSig = (arr) => (arr || []).map((d) => `${d.id}:${d.status}`).join("|");

const STATUS_OPTS = [
  { value: "nuevo", label: "Nuevo" },
  { value: "en_gestion", label: "En gestión" },
  { value: "cerrado", label: "Cerrado" },
];

const NAV = [
  { key: "leads", label: "Leads", short: "Leads", icon: Inbox },
  { key: "seguros", label: "Seguros Activos", short: "Seguros", icon: ShieldCheck },
  { key: "afiliados", label: "Central de Afiliados", short: "Afiliados", icon: Users },
];

const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <div data-testid={`stat-${label}`} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{value}</p>
    <p className="mt-1 text-sm text-gray-500">{label}</p>
    {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
  </div>
);

// Paleta rotativa para los dots de "Leads por origen"
const ORIGEN_COLORS = ["#334155", "#1e3a8a", "#f97316", "#eab308", "#0ea5e9", "#0f172a", "#dc2626", "#64748b", "#6366f1"];
const originColorFor = (key) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return ORIGEN_COLORS[h % ORIGEN_COLORS.length];
};
const originName = (o) => {
  if (o.partner) return o.partner.charAt(0).toUpperCase() + o.partner.slice(1);
  if (o.ref) return `Ref: ${o.ref}`;
  return "Sitio general";
};
const originKey = (o) => o.partner || (o.ref ? `ref:${o.ref}` : "sitio_general");

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">{children}</th>
);
const Td = ({ children }) => <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{children}</td>;

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [industriaFilter, setIndustriaFilter] = useState("todas");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [originFilter, setOriginFilter] = useState("todos");
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const bootedRef = useRef(false);
  const leadsSigRef = useRef("");
  const claimsSigRef = useRef("");

  const fetchAll = async () => {
    try {
      const [s, l, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/claims"),
      ]);
      setStats(s.data);
      const nextLeadsSig = leadSig(l.data);
      if (nextLeadsSig !== leadsSigRef.current) {
        leadsSigRef.current = nextLeadsSig;
        setLeads(l.data);
      }
      const nextClaimsSig = claimSig(c.data);
      if (nextClaimsSig !== claimsSigRef.current) {
        claimsSigRef.current = nextClaimsSig;
        setClaims(c.data);
      }
    } catch (e) {
      if (!bootedRef.current) toast.error("Error cargando datos");
    } finally {
      if (!bootedRef.current) {
        setLoading(false);
        bootedRef.current = true;
      }
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClaimStatus = async (id, status) => {
    try {
      await api.patch(`/admin/claims/${id}/status`, { status });
      toast.success("Estado actualizado");
      fetchAll();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const downloadZip = async (claim) => {
    try {
      const res = await api.get(`/admin/claims/${claim.id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `siniestro_${(claim.nombre || "claim").replace(/\s/g, "_")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get("/admin/export/all.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `operasafe_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el Excel"); }
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const filteredLeads = useMemo(() => {
    let arr = leads;
    if (industriaFilter !== "todas") {
      arr = arr.filter((l) => (l.tipo_industria || []).includes(industriaFilter));
    }
    if (statusFilter !== "todos") {
      arr = arr.filter((l) => l.status === statusFilter);
    }
    if (originFilter !== "todos") {
      arr = arr.filter((l) => {
        const key = l.partner || (l.ref ? `ref:${l.ref}` : "sitio_general");
        return key === originFilter;
      });
    }
    return arr;
  }, [leads, industriaFilter, statusFilter, originFilter]);

  const origenes = useMemo(() => {
    const entries = Object.entries(stats?.by_origen || {});
    return entries
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo.png" alt="OperaSafe.cl" className="h-9 w-auto" />
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-widest text-gray-400">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-gray-500">{admin?.email}</span>
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* NAV pills */}
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex overflow-x-auto gap-1 py-2">
            {NAV.map((n) => {
              const active = section === n.key;
              const Icon = n.icon;
              return (
                <button
                  key={n.key}
                  type="button"
                  data-testid={`nav-${n.key}`}
                  onClick={() => setSection(n.key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-brand-ink text-white shadow" : "text-gray-600 hover:bg-white hover:text-brand-ink"}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{n.label}</span>
                  <span className="sm:hidden">{n.short}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {section === "leads" && (
          <>
            {/* Header con título y subtítulo */}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-3xl font-bold text-brand-ink">Leads y cotizaciones</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Solicitudes de cotización que llegaron por los formularios del sitio.{" "}
                  <span className="text-emerald-600 font-semibold">Se actualiza solo para todo el equipo.</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setNewLeadOpen(true)} data-testid="new-lead-btn"
                  className="bg-brand-ink hover:bg-brand-inkHover text-white">
                  <UserPlus className="h-4 w-4 mr-1" /> Nuevo lead
                </Button>
                <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-leads-btn">
                  <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
                </Button>
              </div>
            </div>

            {/* Stats (5 tarjetas) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard icon={Inbox} label="Leads activos" value={stats?.leads ?? 0} accent="bg-blue-100 text-blue-700" />
              <StatCard icon={CalendarClock} label="Esta semana" value={stats?.leads_week ?? 0} accent="bg-indigo-100 text-indigo-700" />
              <StatCard icon={TrendingUp} label="Conversión" value={`${stats?.conversion ?? 0}%`}
                accent="bg-emerald-100 text-emerald-700" sub={`${stats?.by_status?.ganado ?? 0} emitidos`} />
              <StatCard icon={ShieldAlert} label="Siniestros" value={stats?.claims ?? 0} accent="bg-amber-100 text-amber-700" />
              <StatCard icon={Archive} label="Archivados" value={stats?.archived ?? 0} accent="bg-gray-100 text-gray-600" />
            </div>

            {/* LEADS POR ORIGEN */}
            {origenes.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Leads por origen</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {origenes.map((o) => {
                    const color = originColorFor(o.key);
                    const isActive = originFilter === o.key;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`origen-card-${o.key}`}
                        onClick={() => setOriginFilter(isActive ? "todos" : o.key)}
                        className={`text-left rounded-2xl border bg-white p-4 shadow-sm transition-all hover:shadow-md ${isActive ? "border-brand-yellow ring-2 ring-brand-yellow/30" : "border-gray-200"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                          <span className="text-sm font-semibold text-brand-ink truncate">{originName(o)}</span>
                        </div>
                        <p className="mt-3 font-display text-2xl font-bold text-brand-ink">{o.count}</p>
                        <p className="mt-1 text-[11px] text-gray-500">
                          {o.won || 0} emitidos{o.claims ? ` · ${o.claims} siniestros` : ""}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Distribución por industria */}
            {stats?.by_industria && Object.keys(stats.by_industria).length > 0 && (
              <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Leads por industria</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.by_industria).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/10 text-brand-ink px-3 py-1 text-xs font-semibold">
                      <Factory className="h-3 w-3 text-brand-gold" />
                      {INDUSTRIA_LABELS[k] || k}: <span className="font-bold">{v}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Fila de filtros por estado + selector origen + Excel */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  data-testid="status-filter-todos"
                  onClick={() => setStatusFilter("todos")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors border ${statusFilter === "todos" ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-gray-200 text-gray-600 hover:border-brand-ink"}`}
                >Todos: {leads.length}</button>
                {[
                  { k: "nuevo", label: "Nuevo", cls: "bg-blue-100 text-blue-700 border-blue-200" },
                  { k: "simulacion_lista", label: "En cotización", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
                  { k: "simulacion_enviada", label: "Simulación enviada", cls: "bg-violet-100 text-violet-700 border-violet-200" },
                  { k: "en_emision", label: "En emisión", cls: "bg-amber-100 text-amber-700 border-amber-200" },
                  { k: "ganado", label: "Emitido", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
                  { k: "perdido", label: "Perdido", cls: "bg-rose-100 text-rose-700 border-rose-200" },
                ].map((s) => {
                  const n = stats?.by_status?.[s.k] ?? 0;
                  const active = statusFilter === s.k;
                  return (
                    <button
                      key={s.k}
                      type="button"
                      data-testid={`status-filter-${s.k}`}
                      onClick={() => setStatusFilter(active ? "todos" : s.k)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors border ${active ? s.cls + " ring-2 ring-offset-1 ring-brand-yellow" : s.cls + " opacity-70 hover:opacity-100"}`}
                    >{s.label}: {n}</button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Select value={originFilter} onValueChange={setOriginFilter}>
                  <SelectTrigger data-testid="origin-select" className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todos los orígenes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los orígenes</SelectItem>
                    {origenes.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{originName(o)} ({o.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={downloadXlsx} data-testid="export-xlsx-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow">
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Descargar Excel (todo)
                </Button>
              </div>
            </div>

            <div className="mt-6">
              <Tabs defaultValue="leads">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <TabsList data-testid="section-leads-tabs">
                    <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
                    <TabsTrigger value="claims" data-testid="tab-claims">Siniestros ({claims.length})</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="leads">
                  {/* Filtro por industria (chips) */}
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide shrink-0">Industria:</span>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setIndustriaFilter("todas")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${industriaFilter === "todas" ? "bg-brand-ink text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-brand-yellow"}`}
                      >Todas ({leads.length})</button>
                      {Object.entries(INDUSTRIA_LABELS).map(([k, label]) => {
                        const count = leads.filter((l) => (l.tipo_industria || []).includes(k)).length;
                        if (count === 0) return null;
                        return (
                          <button key={k} type="button" onClick={() => setIndustriaFilter(k)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${industriaFilter === k ? "bg-brand-yellow text-brand-ink" : "bg-white border border-gray-200 text-gray-600 hover:border-brand-yellow"}`}
                          >{label} ({count})</button>
                        );
                      })}
                    </div>
                  </div>
                  <LeadsManager leads={filteredLeads} onChange={fetchAll} />
                </TabsContent>

                <TabsContent value="claims">
                  {claims.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Aún no hay siniestros reportados.</div>
                  ) : (
                    <div className="overflow-x-auto bg-white rounded-2xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50"><tr>
                          <Th>Fecha</Th><Th>Nombre</Th><Th>Industria</Th><Th>Equipo</Th><Th>Tipos</Th><Th>Estado</Th><Th>Acciones</Th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {claims.map((c) => (
                            <tr key={c.id} data-testid={`claim-row-${c.id}`}>
                              <Td>{fmtDate(c.created_at)}</Td>
                              <Td>{c.nombre}</Td>
                              <Td>{INDUSTRIA_LABELS[c.tipo_industria] || "-"}</Td>
                              <Td>{c.equipo}</Td>
                              <Td><span className="text-xs">{(c.tipos || []).join(", ")}</span></Td>
                              <Td>
                                <Select value={c.status} onValueChange={(v) => handleClaimStatus(c.id, v)}>
                                  <SelectTrigger data-testid={`claim-status-${c.id}`} className="h-8 w-32"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {STATUS_OPTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </Td>
                              <Td>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))} data-testid={`claim-view-${c.id}`}>
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => downloadZip(c)} data-testid={`claim-zip-${c.id}`}>
                                    <Download className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}

        {section === "seguros" && <ActivePoliciesManager leads={leads} onChange={fetchAll} />}
        {section === "afiliados" && <AffiliatesManager onChange={fetchAll} />}
      </main>

      <NewLeadDialog open={newLeadOpen} onClose={() => setNewLeadOpen(false)} onCreated={fetchAll} />
    </div>
  );
}
